import { useCallback, useEffect, useRef, useState } from 'react';

import type { IntakeFormValues, UUID } from '@aline/common';
import { getIsIntakeFormComplete, IntakeFormDefinitionSchema } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useDebouncedCallback } from 'use-debounce';

const AUTOSAVE_DELAY_MS = 500;

interface IntakeFormSnapshot {
  completedAt: string | null;
  formDefinition: unknown;
  values: IntakeFormValues;
}

/**
 * Local values state for a launched workflow's intake form, with debounced
 * autosave. Each save sends only the fields that changed (a delta), so a
 * concurrent editor, tab, or AI agent writing other fields is never silently
 * reverted by this client re-sending a stale full map; the server merges the
 * delta over the current row. Signature readiness is a heavy fan-out, so it is
 * refetched only when this client predicts a completion transition (it runs the
 * same shared validation the server uses). A pending save is flushed on unmount
 * so navigating away within the debounce window never drops the last edit (or a
 * just-uploaded file reference).
 */
export function useIntakeFormValues(workflowId: UUID, intakeForm: IntakeFormSnapshot) {
  const pendingPatchRef = useRef<IntakeFormValues>({});
  const savePromiseRef = useRef<Promise<void> | null>(null);
  const valuesRef = useRef<IntakeFormValues>(intakeForm.values);
  const wasCompleteRef = useRef(Boolean(intakeForm.completedAt));
  const [updateValues] = Apollo.useUpdateIntakeFormValuesMutation();
  const [values, setValues] = useState<IntakeFormValues>(intakeForm.values);
  const formDefinitionRef = useRef<unknown>(intakeForm.formDefinition);
  formDefinitionRef.current = intakeForm.formDefinition;

  const savePendingPatch = useCallback(() => {
    if (savePromiseRef.current) return savePromiseRef.current;

    const savePromise = savePendingPatchLoop();
    savePromiseRef.current = savePromise;
    void savePromise.finally(() => {
      if (savePromiseRef.current === savePromise) savePromiseRef.current = null;
    });

    return savePromise;

    async function savePendingPatchLoop() {
      while (Object.keys(pendingPatchRef.current).length > 0) {
        const patch = pendingPatchRef.current;
        pendingPatchRef.current = {};

        const parsed = IntakeFormDefinitionSchema.safeParse(formDefinitionRef.current);
        const nowComplete = parsed.success
          ? getIsIntakeFormComplete(parsed.data, valuesRef.current)
          : wasCompleteRef.current;
        const refreshReadiness = nowComplete !== wasCompleteRef.current;
        const prevWasComplete = wasCompleteRef.current;
        wasCompleteRef.current = nowComplete;

        try {
          await updateValues({
            variables: { input: { values: patch, workflowId }, refreshReadiness },
          });
        } catch {
          pendingPatchRef.current = { ...patch, ...pendingPatchRef.current };
          wasCompleteRef.current = prevWasComplete;
          return;
        }
      }
    }
  }, [updateValues, workflowId]);
  const debouncedSave = useDebouncedCallback(savePendingPatch, AUTOSAVE_DELAY_MS);
  const flushPendingValues = useCallback(async () => {
    debouncedSave.flush();
    await savePendingPatch();
  }, [debouncedSave, savePendingPatch]);
  const setFieldValue = useCallback(
    (name: string, value: unknown) => {
      const next = { ...valuesRef.current, [name]: value };
      valuesRef.current = next;
      pendingPatchRef.current = { ...pendingPatchRef.current, [name]: value };
      setValues(next);
      debouncedSave();
    },
    [debouncedSave]
  );

  useEffect(() => {
    return () => void flushPendingValues();
  }, [flushPendingValues]);

  return { flushPendingValues, setFieldValue, values };
}
